import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../auth/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { DollarSign, Heart, Sparkles, Users, Mountain, Plus, Check, MessageSquare, Flame, LogOut, Mic, RefreshCw, Brain } from "lucide-react";
import { toast } from "sonner";
import RoadmapGantt from "../components/RoadmapGantt";
import NudgesBanner from "../components/NudgesBanner";
import WeeklyInsights from "../components/WeeklyInsights";
import Reminders from "../components/Reminders";
import SubscriptionBanner from "../components/SubscriptionBanner";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../components/ui/tooltip";
import { Lock, Share2, Send } from "lucide-react";
import ShareDialog from "../components/ShareDialog";
import AvatarGreetingCard from "../components/AvatarGreetingCard";
import { celebrate } from "../lib/celebrate";
import { track } from "../lib/analytics";

const PILLAR_META = {
  financial: { icon: DollarSign, color: "#3e6b52", bg: "#e9f0ec", label: "Financial" },
  health: { icon: Heart, color: "#b94e4e", bg: "#f5e6e6", label: "Health" },
  spirituality: { icon: Sparkles, color: "#7a5fb0", bg: "#ede8f5", label: "Spirituality" },
  relationships: { icon: Users, color: "#c18848", bg: "#f5ece0", label: "Relationships" },
  endgame: { icon: Mountain, color: "#486a8f", bg: "#e7edf5", label: "Endgame" },
};

const QUOTES = [
  "Awareness is the first act of transformation.",
  "You don’t rise to your goals. You fall to your systems.",
  "Presence is the discipline of coming back.",
  "Small daily choices compound into a life.",
  "Clarity is a practice, not a moment.",
];

export default function DashboardPage() {
  const { user, onboarding, logout } = useAuth();
  const navigate = useNavigate();
  const [habits, setHabits] = useState([]);
  const [goals, setGoals] = useState([]);
  const [roadmap, setRoadmap] = useState(null);
  const [loadingRoadmap, setLoadingRoadmap] = useState(false);
  const [newHabit, setNewHabit] = useState({ name: "", pillar: "health" });
  const [quote] = useState(QUOTES[Math.floor(Math.random() * QUOTES.length)]);
  const [billing, setBilling] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareMode, setShareMode] = useState("invite");

  // Voice is included free during the trial; on paid Basic it's locked behind Advanced.
  const sub = billing?.subscription || {};
  const onBasicPaid =
    sub.is_active && !sub.is_trial && (sub.plan === "basic" || sub.plan === "basic_annual");
  const voiceLocked = onBasicPaid;

  const maxStreak = habits.reduce((m, h) => Math.max(m, h.streak || 0), 0);

  const openShare = (mode) => {
    setShareMode(mode);
    setShareOpen(true);
    track("share_dialog_opened", { mode });
  };

  const loadAll = async () => {
    try {
      const [h, g, r, b] = await Promise.all([
        axios.get(`${API}/habits`),
        axios.get(`${API}/goals`),
        axios.get(`${API}/roadmap`),
        axios.get(`${API}/billing/me`),
      ]);
      setHabits(h.data.habits || []);
      setGoals(g.data.goals || []);
      setRoadmap(r.data?.content || null);
      setBilling(b.data || null);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const regenerateRoadmap = async () => {
    setLoadingRoadmap(true);
    try {
      const r = await axios.post(`${API}/roadmap/generate`, {});
      setRoadmap(r.data);
      toast.success("Your roadmap is refreshed.");
      celebrate("normal");
      track("roadmap_generated");
    } catch (e) {
      toast.error("Couldn't generate roadmap.");
    } finally {
      setLoadingRoadmap(false);
    }
  };

  const addHabit = async () => {
    if (!newHabit.name.trim()) return;
    try {
      const r = await axios.post(`${API}/habits`, newHabit);
      setHabits((h) => [...h, r.data]);
      setNewHabit({ name: "", pillar: "health" });
      toast.success("Habit added.");
    } catch (e) {
      toast.error("Couldn't add habit.");
    }
  };

  const completeHabit = async (id) => {
    try {
      const r = await axios.post(`${API}/habits/${id}/complete`, {});
      setHabits((hs) => hs.map((h) => (h.habit_id === id ? r.data : h)));
      const newStreak = r.data?.streak || 0;
      track("habit_completed", { habit_id: id, streak: newStreak });
      // Celebrate streak milestones
      if (newStreak > 0 && [3, 7, 14, 21, 30, 60, 90, 180, 365].includes(newStreak)) {
        celebrate("big");
        toast.success(`${newStreak}-day streak! Keep going.`);
        track("streak_reached", { streak: newStreak });
      } else if (newStreak > 0) {
        celebrate("normal");
      }
    } catch (e) {
      toast.error("Couldn't update.");
    }
  };

  const todayStr = new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });

  return (
    <div className="min-h-screen bg-[#f7f3ea]">
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-[#f7f3ea]/85 backdrop-blur-md border-b border-[#e9e0cd]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-[64px] sm:h-[68px] flex items-center justify-between gap-2">
          <Link to="/dashboard" className="flex items-center gap-1 min-w-0">
            <span className="font-display text-[20px] font-semibold text-[#0f1724]">ascend</span>
            <span className="font-display text-[20px] font-semibold text-[#486a8f]">higher</span>
          </Link>
          <div className="flex items-center gap-2 shrink-0">
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    data-testid="dashboard-talk-coach-btn"
                    onClick={() => navigate(voiceLocked ? "/pricing" : "/coach/voice")}
                    className={`h-10 px-3 sm:px-4 rounded-full font-medium text-[13px] btn-lift ${
                      voiceLocked
                        ? "bg-[#e7edf5] hover:bg-[#dae3ef] text-[#486a8f] border border-[#c5d2e3]"
                        : "bg-[#486a8f] hover:bg-[#3a5a7c] text-white"
                    }`}
                  >
                    {voiceLocked ? (
                      <>
                        <Lock size={13} className="sm:mr-2" /> <span className="hidden sm:inline">Talk to Coach</span>
                      </>
                    ) : (
                      <>
                        <Mic size={15} className="sm:mr-2" /> <span className="hidden sm:inline">Talk to Coach</span>
                      </>
                    )}
                  </Button>
                </TooltipTrigger>
                {voiceLocked && (
                  <TooltipContent side="bottom" className="max-w-[240px] text-center">
                    Voice coaching is included with Advanced. Click to upgrade.
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
            {user?.picture ? (
              <img src={user.picture} alt={user.name} className="w-9 h-9 rounded-full" />
            ) : (
              <div className="w-9 h-9 rounded-full bg-[#486a8f] flex items-center justify-center text-white font-display">
                {user?.name?.[0] || "U"}
              </div>
            )}
            <Button data-testid="dashboard-logout-btn" aria-label="Log out" title="Log out" variant="ghost" size="icon" onClick={logout} className="rounded-full text-[#6b7280] hover:text-[#0f1724]">
              <LogOut size={16} />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-6 md:py-10">
        <p className="font-mono-eyebrow text-[#486a8f] mb-3">{todayStr}</p>
        <AvatarGreetingCard />

        <NudgesBanner />
        <SubscriptionBanner />

        {/* Grid */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Roadmap - big column */}
          <section className="lg:col-span-2 rounded-2xl bg-white border border-[#eadfc9] p-5 sm:p-6 md:p-8">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-6 gap-3">
              <div className="min-w-0">
                <p className="font-mono-eyebrow text-[#486a8f] mb-2">Your lifetime roadmap</p>
                <h2 className="font-display text-[22px] sm:text-[26px] md:text-[32px] text-[#0f1724] tracking-tight leading-tight break-words">
                  {roadmap?.endgame_vision || "Every horizon laddering up to your endgame"}
                </h2>
                {roadmap?.first_next_step && (
                  <p className="font-body text-[13px] text-[#486a8f] mt-2 italic">
                    Today: {roadmap.first_next_step}
                  </p>
                )}
              </div>
              <div className="flex flex-wrap gap-2 shrink-0">
                <Button
                  onClick={regenerateRoadmap}
                  disabled={loadingRoadmap}
                  variant="ghost"
                  className="rounded-full text-[#486a8f] hover:bg-[#e7edf5]"
                >
                  <RefreshCw size={14} className={`mr-2 ${loadingRoadmap ? "animate-spin" : ""}`} />
                  {loadingRoadmap ? "Generating…" : "Refresh"}
                </Button>
                <Button
                  data-testid="dashboard-share-roadmap-btn"
                  onClick={() => openShare("roadmap")}
                  variant="ghost"
                  className="rounded-full text-[#3e6b52] hover:bg-[#e9f0ec]"
                >
                  <Share2 size={14} className="mr-2" /> Share
                </Button>
              </div>
            </div>

            {roadmap ? (
              <RoadmapGantt roadmap={roadmap} onChange={setRoadmap} />
            ) : (
              <div className="text-center py-16">
                <p className="font-body text-[#6b7280] mb-4">Your lifetime roadmap is being crafted.</p>
                <Button onClick={regenerateRoadmap} className="rounded-full bg-[#486a8f] hover:bg-[#3a5a7c] text-white">
                  Generate now
                </Button>
              </div>
            )}
          </section>

          {/* Right column */}
          <div className="space-y-6">
            {/* Habits */}
            <section className="rounded-2xl bg-white border border-[#eadfc9] p-6">
              <div className="flex items-center justify-between mb-4">
                <p className="font-mono-eyebrow text-[#486a8f]">Daily habits</p>
                <Flame size={14} className="text-[#c18848]" />
              </div>
              <div className="space-y-2 mb-4">
                {habits.length === 0 && (
                  <p className="font-body text-sm text-[#6b7280]">No habits yet. Add one below.</p>
                )}
                {habits.map((h) => {
                  const meta = PILLAR_META[h.pillar] || PILLAR_META.endgame;
                  const Icon = meta.icon;
                  const today = new Date().toDateString();
                  const lastDone = h.last_completed ? new Date(h.last_completed).toDateString() : null;
                  const doneToday = lastDone === today;
                  return (
                    <button
                      key={h.habit_id}
                      onClick={() => completeHabit(h.habit_id)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border text-left transition-colors ${doneToday ? "bg-[#f0e9d6] border-[#d9cfb8]" : "bg-white border-[#eadfc9] hover:bg-[#faf6ec]"}`}
                    >
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: meta.bg }}>
                        <Icon size={14} style={{ color: meta.color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`font-body text-[13px] font-medium text-[#0f1724] ${doneToday ? "line-through opacity-60" : ""}`}>
                          {h.name}
                        </p>
                        <p className="font-body text-[11px] text-[#6b7280]">Streak {h.streak || 0} · {meta.label}</p>
                      </div>
                      {doneToday && <Check size={14} className="text-[#3e6b52]" />}
                    </button>
                  );
                })}
              </div>
              <div className="flex gap-2">
                <Input
                  value={newHabit.name}
                  onChange={(e) => setNewHabit((n) => ({ ...n, name: e.target.value }))}
                  onKeyDown={(e) => e.key === "Enter" && addHabit()}
                  placeholder="New habit…"
                  className="h-9 text-[13px]"
                />
                <select
                  value={newHabit.pillar}
                  onChange={(e) => setNewHabit((n) => ({ ...n, pillar: e.target.value }))}
                  className="h-9 px-2 rounded-md border border-[#e6dcc6] bg-white text-[12px] text-[#2b3442]"
                >
                  {Object.entries(PILLAR_META).map(([k, v]) => (
                    <option key={k} value={k}>{v.label}</option>
                  ))}
                </select>
                <Button onClick={addHabit} size="icon" className="h-9 w-9 rounded-md bg-[#486a8f] hover:bg-[#3a5a7c]">
                  <Plus size={15} />
                </Button>
              </div>
            </section>

            {/* Pillars quick view */}
            <section className="rounded-2xl bg-white border border-[#eadfc9] p-6">
              <p className="font-mono-eyebrow text-[#486a8f] mb-4">Your 5 pillars</p>
              <div className="space-y-2">
                {Object.entries(PILLAR_META).map(([key, meta]) => {
                  const Icon = meta.icon;
                  const snap = onboarding?.[key] || {};
                  const summary = Object.values(snap).filter(Boolean).slice(0, 1).join(" ");
                  return (
                    <div key={key} className="flex items-center gap-3 p-3 rounded-xl bg-[#faf6ec] border border-[#f2ecdc]">
                      <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: meta.bg }}>
                        <Icon size={15} style={{ color: meta.color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-body text-[13px] font-medium text-[#0f1724]">{meta.label}</p>
                        <p className="font-body text-[11px] text-[#6b7280] truncate">
                          {summary || "Not set"}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>

            {/* Coach CTA — voice-first (or locked if on paid Basic) */}
            <section className="rounded-2xl bg-gradient-to-br from-[#0f1724] to-[#1c2944] text-white p-6">
              <p className="font-mono-eyebrow text-[#8aa7c4] mb-3">
                {voiceLocked ? "Voice is in Advanced" : "Your coach is on the line"}
              </p>
              <h3 className="font-display text-[22px] tracking-tight leading-tight mb-2">
                {voiceLocked ? "Unlock voice coaching." : "One tap. Real conversation."}
              </h3>
              <p className="font-body text-[13px] text-white/75 mb-5 leading-relaxed">
                {voiceLocked
                  ? "Hands-free calls with deeper memory and weekly insights."
                  : "Pick up a voice call with your coach — they remember every session."}
              </p>
              {voiceLocked ? (
                <Button
                  data-testid="dashboard-voice-locked-cta"
                  onClick={() => navigate("/pricing")}
                  className="w-full h-11 rounded-full bg-white hover:bg-[#faf6ec] text-[#0f1724] text-[14px] font-medium btn-lift mb-2"
                >
                  <Lock size={14} className="mr-2" /> Upgrade to unlock voice
                </Button>
              ) : (
                <Button
                  data-testid="dashboard-voice-coach-cta"
                  onClick={() => navigate("/coach/voice")}
                  className="w-full h-11 rounded-full bg-[#3e6b52] hover:bg-[#345a45] text-white text-[14px] font-medium btn-lift mb-2"
                >
                  <Mic size={15} className="mr-2" /> Start the call
                </Button>
              )}
              <button
                data-testid="dashboard-text-coach-cta"
                onClick={() => navigate("/coach")}
                className="w-full h-10 rounded-full bg-white/10 hover:bg-white/15 border border-white/15 text-white/85 text-[12px] font-medium inline-flex items-center justify-center gap-2 transition-colors mb-3"
              >
                <MessageSquare size={13} /> {voiceLocked ? "Message in text" : "Or message in text"}
              </button>
              <button
                onClick={() => navigate("/practices")}
                className="w-full h-10 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-white/75 text-[12px] font-medium inline-flex items-center justify-center gap-2 transition-colors"
              >
                <Brain size={13} /> Awareness & clarity practices
              </button>
            </section>

            <Reminders />

            <WeeklyInsights />

            {/* Invite a friend */}
            <section className="rounded-2xl bg-[#faf6ec] border border-[#eadfc9] p-6">
              <p className="font-mono-eyebrow text-[#486a8f] mb-3">Grow your circle</p>
              <h3 className="font-display text-[20px] tracking-tight leading-tight text-[#0f1724] mb-2">
                Invite a friend.
              </h3>
              <p className="font-body text-[13px] text-[#4b5563] mb-4 leading-relaxed">
                Working on yourself is easier together. Send a friend your invite link.
              </p>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  data-testid="dashboard-invite-friend-btn"
                  onClick={() => openShare("invite")}
                  className="h-10 rounded-full bg-[#486a8f] hover:bg-[#3a5a7c] text-white text-[12px] font-medium"
                >
                  <Send size={13} className="mr-2" /> Invite
                </Button>
                {maxStreak >= 3 && (
                  <Button
                    data-testid="dashboard-share-streak-btn"
                    onClick={() => openShare("streak")}
                    variant="outline"
                    className="h-10 rounded-full border-[#d9cfb8] text-[#2b3442] text-[12px] font-medium"
                  >
                    <Flame size={13} className="mr-2 text-[#c18848]" /> Share streak
                  </Button>
                )}
              </div>
            </section>
          </div>
        </div>
      </main>
      <ShareDialog
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        mode={shareMode}
        streak={maxStreak}
      />
    </div>
  );
}
